import { useState, useEffect } from "react";
import { trackSignUp } from "../lib/ga";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, setAuthToken } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { GoogleLogin } from "@react-oauth/google";
import { readFunderQuizDraft, draftToMandate, FUNDER_QUIZ_STORAGE_KEY } from "../lib/funderQuizMandate";
import { PROJECT_QUIZ_STORAGE_KEY, readProjectQuizDraft } from "../lib/projectQuiz";
import { applyFounderQuizDraft } from "../lib/founderQuizDraft";
import { convertQuizCompletion } from "../components/quiz/ShareScore";

// Project pathway: if the founder arrived via the "Who can fund my project?"
// quiz, convert/create their project venture + seed the PRS intake from the
// draft. `force` bootstraps a blank project even without a quiz draft (used
// when the person picks "For Projects" directly on the signup form).
// Returns true when the founder should land on /project/intake.
async function applyProjectQuizAnswers(apiClient, force = false) {
  const draft = readProjectQuizDraft();
  const hasDraft = !!(draft && draft.completed_at);
  if (!hasDraft && !force) return false;
  try {
    await apiClient.post("/project/bootstrap", { quiz: hasDraft ? draft : {} });
    try { localStorage.removeItem(PROJECT_QUIZ_STORAGE_KEY); } catch (_) { /* ignore */ }
    if (hasDraft) convertQuizCompletion(apiClient);
    return true;
  } catch (e) {
    if (process.env.NODE_ENV === "development") console.warn("[Signup] project bootstrap failed", e);
    return false;
  }
}

// Fire-and-forget: pre-fill the new funder's mandate from their pre-signup
// Get Started quiz answers (localStorage draft), then clear the draft.
export async function applyFunderQuizMandate(apiClient) {
  const mandate = draftToMandate(readFunderQuizDraft());
  if (!mandate) return;
  try {
    await apiClient.put("/funder/mandate", mandate);
  } catch (e) {
    if (process.env.NODE_ENV === "development") console.warn("[Signup] mandate prefill failed", e);
  } finally {
    try { localStorage.removeItem(FUNDER_QUIZ_STORAGE_KEY); } catch (_) { /* ignore */ }
  }
}

function formatErr(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || JSON.stringify(d);
}

export default function Signup() {
  const [params] = useSearchParams();
  const roleParam = params.get("role");
  const initialRole = roleParam === "funder" ? "funder"
    : roleParam === "project" ? "project"
    : (readProjectQuizDraft()?.completed_at ? "project" : "founder");
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const quizRecap = (() => {
    if (role !== "founder") return null;
    try {
      const d = JSON.parse(localStorage.getItem("clinq_get_started_v2") || "null");
      if (!d?.completed_at) return null;
      return d.preliminary_score != null
        ? `Your quiz result (${d.preliminary_score}/100) is saved — it carries into your account.`
        : "Your quiz answers are saved — they carry into your account.";
    } catch (_) { return null; }
  })();
  const [pendingVerify, setPendingVerify] = useState(null); // {email} — check-your-inbox state
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent
  const nav = useNavigate();
  const { refresh } = useAuth();

  // Referral attribution
  const refCode = (params.get("ref") || "").trim().toUpperCase().slice(0, 20);
  const [refInfo, setRefInfo] = useState(null); // {valid, referrer_display_name, bonus_copy}

  // Studio cohort invite
  const studioInvite = (params.get("studio_invite") || "").trim();
  const [studioInviteInfo, setStudioInviteInfo] = useState(null); // {valid, email, studio_name}

  useEffect(() => {
    if (!studioInvite) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/studio-invite/${encodeURIComponent(studioInvite)}`);
        if (!cancelled) {
          setStudioInviteInfo(data);
          if (data?.email) setForm((f) => (f.email ? f : { ...f, email: data.email }));
        }
      } catch (_e) {
        if (!cancelled) setStudioInviteInfo({ valid: false });
      }
    })();
    return () => { cancelled = true; };
  }, [studioInvite]);

  useEffect(() => {
    if (!refCode) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/referrals/code/${encodeURIComponent(refCode)}`);
        if (!cancelled) setRefInfo(data);
      } catch (_e) {
        if (!cancelled) setRefInfo({ valid: false });
      }
    })();
    return () => { cancelled = true; };
  }, [refCode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...form, role: role === "funder" ? "funder" : "founder" };
      if (role !== "funder") payload.venture_kind = role === "project" ? "project" : "business";
      if (role !== "funder" && refCode) payload.referral_code = refCode;
      if (role !== "funder" && studioInvite) payload.studio_invite = studioInvite;
      const { data } = await api.post("/auth/signup", payload);
      trackSignUp("email", { role: payload.role, source: "signup_page" });
      if (data?.verification_required) {
        // Founder signups must verify their email first — remember the chosen
        // pathway so the verify page can route them correctly after login.
        try { localStorage.setItem("clinq_pending_signup_role", role); } catch (_) { /* ignore */ }
        setPendingVerify({ email: data.email });
        return;
      }
      if (data?.token) setAuthToken(data.token);
      await refresh();
      if (data.role === "funder") { await applyFunderQuizMandate(api); nav("/funder/terminal"); }
      else {
        const isProject = await applyProjectQuizAnswers(api, role === "project");
        if (!isProject) await applyFounderQuizDraft(api);
        nav(isProject ? "/project/intake" : "/onboarding");
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // Direct Google OAuth via @react-oauth/google. The `<GoogleLogin>` button
  // returns a Google-signed JWT credential which we POST to /api/auth/google
  // for server-side signature verification, then our standard JWT cookie is set.
  //
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const onGoogleSuccess = async (credResp) => {
    setErr(""); setBusy(true);
    try {
      const payload = { credential: credResp.credential, role: role === "funder" ? "funder" : "founder" };
      if (role !== "funder") payload.venture_kind = role === "project" ? "project" : "business";
      if (role !== "funder" && refCode) payload.referral_code = refCode;
      if (role !== "funder" && studioInvite) payload.studio_invite = studioInvite;
      const { data } = await api.post("/auth/google", payload);
      trackSignUp("google", { role: payload.role, source: "signup_page" });
      if (data?.token) setAuthToken(data.token);
      await refresh();
      if (data.role === "funder") { await applyFunderQuizMandate(api); nav("/funder/terminal"); }
      else {
        const isProject = await applyProjectQuizAnswers(api, role === "project");
        if (!isProject) await applyFounderQuizDraft(api);
        nav(isProject ? "/project/intake" : "/onboarding");
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Google sign-in failed");
    } finally { setBusy(false); }
  };
  const onGoogleError = () => setErr("Google sign-in was cancelled or failed.");

  const resendVerification = async () => {
    setResendState("sending");
    try {
      await api.post("/auth/resend-verification", { email: pendingVerify.email });
      setResendState("sent");
    } catch (_) {
      setResendState("idle");
    }
  };

  if (pendingVerify) {
    return (
      <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
        <div className="mx-auto max-w-md px-4 py-16 sm:px-6 sm:py-24">
          <Link to="/" className="text-2xl font-extrabold text-[#0A8B85]">Clinq</Link>
          <div className="mt-10 rounded-2xl border border-neutral-200 bg-white p-8 text-center" data-testid="signup-check-inbox">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#0A8B85]/10 text-3xl">📬</div>
            <h1 className="mt-5 text-xl font-bold text-neutral-900">Check your inbox</h1>
            <p className="mt-2 text-sm text-neutral-600">
              We&apos;ve sent a verification link to{" "}
              <span className="font-semibold text-neutral-900" data-testid="signup-check-inbox-email">{pendingVerify.email}</span>.
              Click the link in that email to activate your account.
            </p>
            <p className="mt-3 text-xs text-neutral-500">The link expires in 24 hours. Don&apos;t forget to check your spam folder.</p>
            {resendState === "sent" ? (
              <p className="mt-6 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700" data-testid="signup-resend-sent">
                Sent! A fresh verification email is on its way.
              </p>
            ) : (
              <button data-testid="signup-resend-btn" onClick={resendVerification} disabled={resendState === "sending"}
                className="mt-6 w-full rounded-lg border border-[#0A8B85] py-3 text-sm font-semibold text-[#0A8B85] hover:bg-[#0A8B85]/5 disabled:opacity-50">
                {resendState === "sending" ? "Sending…" : "Resend verification email"}
              </button>
            )}
            <p className="mt-6 text-sm text-neutral-600">
              Already verified?{" "}
              <Link to="/login" className="font-semibold text-[#0A8B85] hover:underline" data-testid="signup-check-inbox-login">Log in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="mx-auto max-w-md px-4 py-12 sm:px-6 sm:py-16">
        <Link to="/" className="text-2xl font-extrabold text-[#0A8B85]" data-testid="signup-logo">Clinq</Link>
        <h1 className="mt-6 text-2xl font-bold text-neutral-900 sm:mt-8 sm:text-3xl">Create your account</h1>
        <p className="mt-2 text-sm text-neutral-600" data-testid="signup-subtitle">
          {role === "funder"
            ? "For Funders — discover Capital-Ready African SMEs."
            : role === "project"
            ? "For Projects — get your capital project funder-ready."
            : "For Businesses — get Capital-Ready in minutes."}
        </p>
        {quizRecap && (
          <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800" data-testid="signup-quiz-recap">
            {quizRecap}
          </p>
        )}
        <p className="hidden">
        </p>

        {/* Studio cohort invite banner */}
        {role !== "funder" && studioInvite && studioInviteInfo?.valid && (
          <div data-testid="signup-studio-invite-banner"
            className="mt-5 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-200 text-emerald-900 font-bold">✓</div>
            <div className="text-emerald-900">
              <div className="font-semibold">{studioInviteInfo.studio_name} invited you to their fundraising cohort</div>
              <div className="text-xs text-emerald-800/90">Sign up and your company is automatically added to their portfolio — they'll track your progress and make warm funder introductions.</div>
            </div>
          </div>
        )}
        {role !== "funder" && studioInvite && studioInviteInfo && studioInviteInfo.valid === false && (
          <div data-testid="signup-studio-invite-invalid"
            className="mt-5 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-2 text-xs text-neutral-600">
            That studio invite link is invalid or has expired. You can still sign up — ask your studio to re-send the invite.
          </div>
        )}

        {/* Referral banner — only shown for founder signups with a valid ?ref= code */}
        {role !== "funder" && refCode && refInfo?.valid && (
          <div data-testid="signup-referral-banner"
            className="mt-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-900 font-bold">★</div>
            <div className="text-amber-900">
              <div className="font-semibold">Invited by {refInfo.referrer_display_name}</div>
              <div className="text-xs text-amber-800/90">{refInfo.bonus_copy}</div>
            </div>
          </div>
        )}
        {role !== "funder" && refCode && refInfo && refInfo.valid === false && (
          <div data-testid="signup-referral-invalid"
            className="mt-5 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-2 text-xs text-neutral-600">
            That referral code didn&apos;t match an active founder. You can still sign up — no bonus applied.
          </div>
        )}

        {/* Role toggle */}
        <div className="mt-6 grid grid-cols-3 rounded-xl border border-neutral-200 bg-white p-1">
          {["founder", "project", "funder"].map(r => (
            <button key={r} type="button" data-testid={`signup-role-${r}`}
              onClick={() => setRole(r)}
              className={"py-2 rounded-lg text-sm font-semibold transition-colors " +
                (role === r ? "bg-[#0A8B85] text-white" : "text-neutral-600 hover:text-neutral-900")}>
              {r === "founder" ? "For Businesses" : r === "project" ? "For Projects" : "For Funders"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <input data-testid="signup-name" placeholder="Full name" required value={form.name}
            onChange={e => setForm({...form, name: e.target.value})}
            className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]" />
          <input data-testid="signup-email" type="email" placeholder="Email" required value={form.email}
            onChange={e => setForm({...form, email: e.target.value})}
            className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]" />
          <input data-testid="signup-password" type="password" placeholder="Password (min 6 chars)" required minLength={6} value={form.password}
            onChange={e => setForm({...form, password: e.target.value})}
            className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]" />
          {err && <p className="text-sm text-red-600" data-testid="signup-error">{err}</p>}
          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-neutral-600">
            <input
              data-testid="signup-consent-checkbox"
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#0A8B85]"
            />
            <span data-testid="signup-consent-text">
              I agree to the{" "}
              <Link to="/terms" target="_blank" className="font-semibold text-[#0A8B85] hover:underline" data-testid="signup-terms-link">Terms of Service</Link>
              {" "}&amp;{" "}
              <Link to="/privacy-policy" target="_blank" className="font-semibold text-[#0A8B85] hover:underline" data-testid="signup-privacy-link">Privacy Policy</Link>
            </span>
          </label>
          <button data-testid="signup-submit" disabled={busy || !agreed} type="submit"
            className="w-full rounded-lg bg-[#0A8B85] py-3 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50">
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>

        <div className="my-5 flex items-center gap-3 text-xs text-neutral-400">
          <div className="h-px flex-1 bg-neutral-200" /> OR <div className="h-px flex-1 bg-neutral-200" />
        </div>
        <div className="flex max-w-full justify-center overflow-hidden" data-testid="signup-google">
          {agreed ? (
            <GoogleLogin
              onSuccess={onGoogleSuccess}
              onError={onGoogleError}
              text="signup_with"
              shape="rectangular"
              theme="outline"
              size="large"
              width={Math.min(384, (typeof window !== "undefined" ? window.innerWidth : 384) - 96)}
            />
          ) : (
            <button
              type="button"
              data-testid="signup-google-blocked"
              onClick={() => setErr("Please agree to the Terms of Service & Privacy Policy first.")}
              className="w-full max-w-sm rounded-md border border-neutral-300 bg-white py-2.5 text-sm font-medium text-neutral-800 hover:bg-neutral-50"
            >
              Sign up with Google <span className="ml-1 text-xs font-normal text-neutral-500">· tick the Terms box above first</span>
            </button>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-neutral-600">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-[#0A8B85] hover:underline" data-testid="signup-to-login">Log in</Link>
        </p>
      </div>
    </div>
  );
}
